import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePetsStore } from '../store'
import { useMedicationsStore } from '../../medications/store'
import { useCheckupsStore } from '../../checkups/store'
import { useVaccinesStore } from '../../vaccines/store'
import { useTrackingStore } from '../../tracking/store'
import { Card, CardContent, CardHeader } from '../../../shared/components/Card'
import { EmptyState } from '../../../shared/components/EmptyState'
import { useConfirm } from '../../../shared/components/Modal'
import { useSuccessToast } from '../../../shared/components/Toast'
import { PageLoader } from '../../../shared/components/Loading'
import { Button } from '../../../shared/components/Button'
import { ResponsiveLayout } from '../../../shared/components/layout'
import { StatusCard, Badge, ActionButton } from '../../../shared/components/widgets'
import { useAuthStore } from '../../auth/store'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Dog,
  Cat,
  PawPrint,
  Pill,
  Stethoscope,
  Syringe,
  Weight,
  Activity,
  FileText,
  Calendar,
  Scale,
  ClipboardPlus,
  Plus,
} from 'lucide-react'
import { ClinicalEventForm } from '../../tracking/components/ClinicalEventForm'
import { formatDateTime, formatTime } from '../../../shared/lib/date-utils'
import type { ClinicalEventCreate } from '@rondela/shared'

function calculateAge(birthDate: string): string {
  const birth = new Date(birthDate)
  const now = new Date()
  const diffMs = now.getTime() - birth.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 30) {
    return `${diffDays} días`
  }

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) {
    return `${diffMonths} ${diffMonths === 1 ? 'mes' : 'meses'}`
  }

  const years = Math.floor(diffMonths / 12)
  const remainingMonths = diffMonths % 12

  if (remainingMonths === 0) {
    return `${years} ${years === 1 ? 'año' : 'años'}`
  }

  return `${years} ${years === 1 ? 'año' : 'años'}, ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`
}

type Tab = 'info' | 'medicamentos' | 'controles' | 'vacunas' | 'historial'

export function PetDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { currentPet, isLoading, error, fetchPet, deletePet, clearCurrentPet } = usePetsStore()
  const { medications, fetchMedicationsByPet } = useMedicationsStore()
  const { checkups, fetchCheckupsByPet } = useCheckupsStore()
  const { vaccines, fetchVaccinesByPet } = useVaccinesStore()
  const { timeline, fetchTimeline, fetchClinicalEvents, createClinicalEvent, isLoading: trackingLoading } = useTrackingStore()
  const [activeTab, setActiveTab] = useState<Tab>('info')
  const { confirm, ConfirmDialog } = useConfirm()
  const showSuccess = useSuccessToast()
  const [isDeleting, setIsDeleting] = useState(false)
  const [showClinicalForm, setShowClinicalForm] = useState(false)

  useEffect(() => {
    if (id) {
      fetchPet(id)
      fetchMedicationsByPet(id)
      fetchCheckupsByPet(id)
      fetchVaccinesByPet(id)
      fetchTimeline(id)
      fetchClinicalEvents(id)
    }
    return () => clearCurrentPet()
  }, [id, fetchPet, fetchMedicationsByPet, fetchCheckupsByPet, fetchVaccinesByPet, fetchTimeline, clearCurrentPet])

  const handleDelete = async () => {
    if (!currentPet) return

    const confirmed = await confirm({
      title: 'Eliminar mascota',
      message: `¿Estás seguro de que quieres eliminar a ${currentPet.nombre}? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    setIsDeleting(true)
    try {
      await deletePet(currentPet.id)
      showSuccess('Mascota eliminada correctamente')
      navigate('/mascotas')
    } catch {
      // Error already handled in store
    } finally {
      setIsDeleting(false)
    }
  }

  const getPetIcon = (especie: string) => {
    switch (especie) {
      case 'perro':
        return <Dog size={32} />
      case 'gato':
        return <Cat size={32} />
      default:
        return <PawPrint size={32} />
    }
  }

  if (isLoading && !currentPet) {
    return (
      <ResponsiveLayout>
        <PageLoader />
      </ResponsiveLayout>
    )
  }

  if (error || !currentPet) {
    return (
      <ResponsiveLayout>
        <div className="page">
          <div className="alert alert-error">{error || 'Mascota no encontrada'}</div>
          <Link to="/mascotas" className="btn btn-secondary">
            Volver
          </Link>
        </div>
      </ResponsiveLayout>
    )
  }

  const isAdmin = user?.role === 'admin'

  return (
    <ResponsiveLayout>
      <div className="page pet-detail-page">
        {ConfirmDialog}
      <div className="page-header">
        <div className="page-header-left">
          <Link to="/mascotas" className="btn btn-ghost">
            <ArrowLeft size={16} />
            Volver
          </Link>
          <div className="pet-header-enhanced">
            <div className="pet-avatar-enhanced">
              {currentPet.fotoUrl ? (
                <img src={currentPet.fotoUrl} alt={currentPet.nombre} />
              ) : (
                <span className="pet-avatar-enhanced__placeholder">
                  {getPetIcon(currentPet.especie)}
                </span>
              )}
            </div>
            <div className="pet-header-enhanced__info">
              <h1>{currentPet.nombre}</h1>
              {currentPet.raza && <p className="pet-header-enhanced__breed">{currentPet.raza}</p>}
              <div className="pet-header-enhanced__stats">
                {currentPet.fechaNacimiento && (
                  <span className="pet-header-enhanced__stat">
                    <Calendar size={14} />
                    {calculateAge(currentPet.fechaNacimiento)}
                  </span>
                )}
                {currentPet.peso && (
                  <span className="pet-header-enhanced__stat">
                    <Scale size={14} />
                    {currentPet.peso} kg
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="page-header-actions">
          <Link to={`/mascotas/${currentPet.id}/editar`} className="btn btn-secondary">
            <Pencil size={16} />
            Editar
          </Link>
          {isAdmin && (
            <Button variant="danger" onClick={handleDelete} loading={isDeleting} icon={Trash2}>
              Eliminar
            </Button>
          )}
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          Info
        </button>
        <button className={`tab ${activeTab === 'medicamentos' ? 'active' : ''}`} onClick={() => setActiveTab('medicamentos')}>
          Medicamentos ({medications.length})
        </button>
        <button className={`tab ${activeTab === 'controles' ? 'active' : ''}`} onClick={() => setActiveTab('controles')}>
          Controles ({checkups.length})
        </button>
        <button className={`tab ${activeTab === 'vacunas' ? 'active' : ''}`} onClick={() => setActiveTab('vacunas')}>
          Vacunas ({vaccines.length})
        </button>
        <button className={`tab ${activeTab === 'historial' ? 'active' : ''}`} onClick={() => setActiveTab('historial')}>
          Historial
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'info' && (
          <div className="pet-info-grid">
            <Card>
              <CardHeader>Información general</CardHeader>
              <CardContent>
                <dl className="info-list">
                  <div className="info-item">
                    <dt>Especie</dt>
                    <dd>{currentPet.especie}</dd>
                  </div>
                  {currentPet.raza && (
                    <div className="info-item">
                      <dt>Raza</dt>
                      <dd>{currentPet.raza}</dd>
                    </div>
                  )}
                  {currentPet.fechaNacimiento && (
                    <>
                      <div className="info-item">
                        <dt>Fecha de nacimiento</dt>
                        <dd>{new Date(currentPet.fechaNacimiento).toLocaleDateString()}</dd>
                      </div>
                      <div className="info-item">
                        <dt>Edad</dt>
                        <dd>{calculateAge(currentPet.fechaNacimiento)}</dd>
                      </div>
                    </>
                  )}
                  {currentPet.peso && (
                    <div className="info-item">
                      <dt>Peso actual</dt>
                      <dd>{currentPet.peso} kg</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
            {currentPet.condicionesMedicas && (
              <Card>
                <CardHeader>Condiciones médicas</CardHeader>
                <CardContent>
                  <p>{currentPet.condicionesMedicas}</p>
                </CardContent>
              </Card>
            )}
            {currentPet.notas && (
              <Card>
                <CardHeader>Notas</CardHeader>
                <CardContent>
                  <p>{currentPet.notas}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'medicamentos' && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Medicamentos</h2>
              <Link to={`/mascotas/${currentPet.id}/medicamentos/nuevo`} className="btn btn-primary btn-sm">
                Agregar
              </Link>
            </div>
            {medications.length === 0 ? (
              <EmptyState
                title="Sin medicamentos"
                description="No hay medicamentos registrados para esta mascota."
                action={
                  <Link to={`/mascotas/${currentPet.id}/medicamentos/nuevo`} className="btn btn-primary">
                    Agregar medicamento
                  </Link>
                }
              />
            ) : (
              <div className="medication-list">
                {medications.map((med) => {
                  const status = med.completada ? 'success' : !med.activa ? 'neutral' : 'info'
                  return (
                    <StatusCard key={med.id} status={status} stripe className="medication-card">
                      <StatusCard.Header>
                        <div className="medication-card__icon">
                          <Pill size={20} />
                        </div>
                        <div className="medication-card__info">
                          <StatusCard.Title>{med.nombre}</StatusCard.Title>
                          <StatusCard.Subtitle>
                            {med.dosis} - {med.frecuencia}
                            {med.horaPreferida && ` (${formatTime(med.horaPreferida)})`}
                          </StatusCard.Subtitle>
                        </div>
                        <div className="medication-card__badges">
                          {!med.activa && <Badge variant="status" status="neutral">Inactivo</Badge>}
                          {med.completada && <Badge variant="status" status="success">Completado</Badge>}
                        </div>
                      </StatusCard.Header>
                      {med.instrucciones && (
                        <StatusCard.Content collapsible={false}>
                          <p className="medication-card__instructions">{med.instrucciones}</p>
                        </StatusCard.Content>
                      )}
                      <StatusCard.Actions>
                        <ActionButton
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/mascotas/${currentPet.id}/medicamentos/${med.id}/editar`)}
                        >
                          Editar
                        </ActionButton>
                      </StatusCard.Actions>
                    </StatusCard>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'controles' && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Controles veterinarios</h2>
              <Link to={`/mascotas/${currentPet.id}/controles/nuevo`} className="btn btn-primary btn-sm">
                Agregar
              </Link>
            </div>
            {checkups.length === 0 ? (
              <EmptyState
                title="Sin controles"
                description="No hay controles veterinarios registrados."
                action={
                  <Link to={`/mascotas/${currentPet.id}/controles/nuevo`} className="btn btn-primary">
                    Registrar control
                  </Link>
                }
              />
            ) : (
              <div className="checkup-list">
                {checkups.map((checkup) => {
                  const isUpcoming = checkup.proximaCita && new Date(checkup.proximaCita) > new Date()
                  return (
                    <StatusCard key={checkup.id} status={isUpcoming ? 'info' : 'neutral'} stripe className="checkup-card">
                      <StatusCard.Header>
                        <div className="checkup-card__icon">
                          <Stethoscope size={20} />
                        </div>
                        <div className="checkup-card__info">
                          <StatusCard.Title>{checkup.motivo}</StatusCard.Title>
                          <StatusCard.Subtitle>
                            {formatDateTime(checkup.fecha, checkup.hora)}
                            {checkup.veterinario && ` · Dr. ${checkup.veterinario}`}
                          </StatusCard.Subtitle>
                        </div>
                        <div className="checkup-card__badges">
                          <Badge variant="pill">{checkup.tipo}</Badge>
                          {isUpcoming && <Badge variant="time">{checkup.proximaCita}</Badge>}
                        </div>
                      </StatusCard.Header>
                      {checkup.diagnostico && (
                        <StatusCard.Content collapsible={false}>
                          <p className="checkup-card__diagnosis">{checkup.diagnostico}</p>
                        </StatusCard.Content>
                      )}
                      <StatusCard.Actions>
                        <ActionButton
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/mascotas/${currentPet.id}/controles/${checkup.id}/editar`)}
                        >
                          Editar
                        </ActionButton>
                      </StatusCard.Actions>
                    </StatusCard>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'vacunas' && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Vacunas</h2>
              <Link to={`/mascotas/${currentPet.id}/vacunas/nueva`} className="btn btn-primary btn-sm">
                Registrar
              </Link>
            </div>
            {vaccines.length === 0 ? (
              <EmptyState
                title="Sin vacunas"
                description="No hay vacunas registradas."
                action={
                  <Link to={`/mascotas/${currentPet.id}/vacunas/nueva`} className="btn btn-primary">
                    Registrar vacuna
                  </Link>
                }
              />
            ) : (
              <div className="vaccine-list">
                {vaccines.map((vaccine) => {
                  const needsRenewal = vaccine.fechaProxima && new Date(vaccine.fechaProxima) <= new Date()
                  const upcomingRenewal = vaccine.fechaProxima && !needsRenewal
                  const status = needsRenewal ? 'warning' : upcomingRenewal ? 'info' : 'success'
                  return (
                    <StatusCard key={vaccine.id} status={status} stripe className="vaccine-card">
                      <StatusCard.Header>
                        <div className="vaccine-card__icon">
                          <Syringe size={20} />
                        </div>
                        <div className="vaccine-card__info">
                          <StatusCard.Title>{vaccine.nombre}</StatusCard.Title>
                          <StatusCard.Subtitle>
                            Aplicada: {formatDateTime(vaccine.fechaAplicacion, vaccine.hora)}
                          </StatusCard.Subtitle>
                        </div>
                        <div className="vaccine-card__badges">
                          {vaccine.fechaProxima && (
                            <Badge
                              variant="time"
                              status={needsRenewal ? 'warning' : undefined}
                            >
                              {needsRenewal ? 'Vencida' : `Próxima: ${vaccine.fechaProxima}`}
                            </Badge>
                          )}
                        </div>
                      </StatusCard.Header>
                    </StatusCard>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'historial' && (
          <div className="tab-section">
            <div className="section-header">
              <h2>Historia Clínica</h2>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowClinicalForm(true)}
              >
                <Plus size={16} />
                Agregar evento
              </Button>
            </div>

            {showClinicalForm && (
              <Card className="clinical-form-card">
                <CardHeader>
                  <h3>Nuevo evento clínico</h3>
                </CardHeader>
                <CardContent>
                  <ClinicalEventForm
                    onSubmit={async (data: ClinicalEventCreate) => {
                      await createClinicalEvent(id!, data)
                      setShowClinicalForm(false)
                      showSuccess('Evento clínico registrado')
                      fetchTimeline(id!)
                    }}
                    onCancel={() => setShowClinicalForm(false)}
                    isLoading={trackingLoading}
                  />
                </CardContent>
              </Card>
            )}

            {timeline.length === 0 && !showClinicalForm ? (
              <EmptyState
                title="Sin historial"
                description="No hay registros en el historial. Agrega el primer evento clínico."
                action={
                  <Button variant="primary" onClick={() => setShowClinicalForm(true)}>
                    <Plus size={16} />
                    Agregar evento
                  </Button>
                }
              />
            ) : (
              <div className="timeline">
                {timeline.map((item) => (
                  <div key={item.id} className="timeline-item">
                    <div className="timeline-icon">
                      {item.type === 'weight' && <Weight size={20} />}
                      {item.type === 'symptom' && <Activity size={20} />}
                      {item.type === 'note' && <FileText size={20} />}
                      {item.type === 'medication_log' && <Pill size={20} />}
                      {item.type === 'checkup' && <Stethoscope size={20} />}
                      {item.type === 'vaccine' && <Syringe size={20} />}
                      {item.type === 'clinical_event' && <ClipboardPlus size={20} />}
                    </div>
                    <div className="timeline-content">
                      <p className="timeline-date">{new Date(item.date).toLocaleDateString()}</p>
                      <p className="timeline-description">
                        {item.type === 'weight' && `Peso: ${item.data.peso} kg`}
                        {item.type === 'symptom' && `Síntoma: ${item.data.descripcion}`}
                        {item.type === 'note' && `Nota: ${item.data.contenido}`}
                        {item.type === 'medication_log' && `Medicamento: ${item.data.medicationNombre}`}
                        {item.type === 'checkup' && `Control: ${item.data.motivo}`}
                        {item.type === 'vaccine' && `Vacuna: ${item.data.nombre}`}
                        {item.type === 'clinical_event' && (
                          <>
                            <strong>{item.data.titulo as string}</strong>
                            {item.data.descripcion && <span> - {item.data.descripcion as string}</span>}
                          </>
                        )}
                      </p>
                      {item.type === 'clinical_event' && (item.data.veterinario as string) && (
                        <p className="timeline-meta">Veterinario: {item.data.veterinario as string}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </ResponsiveLayout>
  )
}
